import React, { useState } from 'react';
import {
  Box, Button, Dialog, DialogContent, LinearProgress,
  Stack, Typography, alpha, useTheme,
} from '@mui/material';
import { ArrowRight, ChevronLeft, Mail, Users, Sparkles, LayoutTemplate, X } from 'lucide-react';

const YOUTUBE_VIDEO_ID = 'iD4CdvIHl9E'; // replace with actual video ID

const STEPS = [
  {
    id: 'welcome',
    icon: Mail,
    iconColor: '#6366f1',
    iconBg: '#eef2ff',
    title: 'Meet Outreach',
    subtitle: 'Send emails to your members and leads, right from DeskOS',
    description:
      'No external tools, no exports. Write an email, pick who gets it, and hit send. Watch the quick intro below to see how it works.',
    video: true,
  },
  {
    id: 'leads',
    icon: Users,
    iconColor: '#0369a1',
    iconBg: '#e0f2fe',
    title: 'Stay in touch with leads',
    subtitle: 'Follow up before they forget about you',
    description:
      'Import leads straight from your pipeline and send them the right email at the right time. A tour invite, a follow-up, a festival offer. Keep the conversation going until they sign.',
    bullets: [
      'Space intro and virtual tour invite',
      'Post-tour follow-up with an offer',
      'Festival campaigns like Diwali, Holi, New Year',
      'Flash promos when you need to fill up fast',
    ],
  },
  {
    id: 'members',
    icon: Sparkles,
    iconColor: '#15803d',
    iconBg: '#dcfce7',
    title: 'Keep your members in the loop',
    subtitle: 'They already love the space, make sure they feel it',
    description:
      'Your members are your best audience. Send a quick newsletter, announce a new cafe, shout out a referral deal, or remind someone their plan is up for renewal.',
    bullets: [
      'Monthly updates and community news',
      'New centre or cafe opening',
      'Referral offers for existing members',
      'Plan renewal reminders and upgrade nudges',
    ],
  },
  {
    id: 'builder',
    icon: LayoutTemplate,
    iconColor: '#b45309',
    iconBg: '#fef3c7',
    title: 'Ready-made templates and a simple builder',
    subtitle: 'You can send your first campaign in under 5 minutes',
    description:
      '15 coworking templates are ready to go. Pick one, fill in your details, and send. If you want something custom, the drag and drop builder has you covered.',
    bullets: [
      '15 templates built for coworking spaces',
      'Drag and drop editor, no design skills needed',
      'Your logo shows up automatically in every email',
      'Send now or schedule for later',
    ],
  },
];

export default function OutreachOnboardingDialog({ open, onClose }) {
  const theme = useTheme();
  const [step, setStep] = useState(0);
  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const isFirst = step === 0;
  const Icon = current.icon;

  const handleNext = () => {
    if (isLast) { onClose(); return; }
    setStep(s => s + 1);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
        },
      }}
    >
      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={((step + 1) / STEPS.length) * 100}
        sx={{
          height: 3,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          '& .MuiLinearProgress-bar': { bgcolor: theme.palette.primary.main },
        }}
      />

      {/* Close */}
      <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}>
        <Box
          onClick={onClose}
          sx={{
            width: 28, height: 28, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            color: 'text.disabled',
            '&:hover': { bgcolor: alpha(theme.palette.action.active, 0.08), color: 'text.primary' },
          }}
        >
          <X size={16} />
        </Box>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Icon + title block */}
        <Box
          sx={{
            px: 4, pt: 4, pb: 3,
            background: `linear-gradient(160deg, ${alpha(current.iconColor, 0.06)} 0%, transparent 60%)`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2} mb={2.5}>
            <Box sx={{
              width: 48, height: 48, borderRadius: 2,
              bgcolor: current.iconBg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Icon size={22} color={current.iconColor} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={800} letterSpacing="-0.4px" lineHeight={1.2}>
                {current.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.25}>
                {current.subtitle}
              </Typography>
            </Box>
          </Stack>

          <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
            {current.description}
          </Typography>
        </Box>

        {/* YouTube embed */}
        {current.video && (
          <Box sx={{ px: 4, pb: 3 }}>
            <Box sx={{
              position: 'relative', paddingTop: '56.25%',
              borderRadius: 2, overflow: 'hidden',
              border: '1px solid', borderColor: 'divider',
              bgcolor: '#000',
            }}>
              <iframe
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
                src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?rel=0&modestbranding=1`}
                title="Outreach intro"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </Box>
          </Box>
        )}

        {/* Bullet points */}
        {current.bullets && (
          <Box sx={{ px: 4, pb: 3 }}>
            <Stack spacing={1.25}>
              {current.bullets.map((b, i) => (
                <Stack key={i} direction="row" alignItems="flex-start" spacing={1.25}>
                  <Box sx={{
                    mt: 0.35, width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                    bgcolor: current.iconBg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: current.iconColor }} />
                  </Box>
                  <Typography variant="body2" color="text.primary" lineHeight={1.6}>{b}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        {/* Footer — step dots + buttons */}
        <Box sx={{
          px: 4, py: 2.5,
          borderTop: '1px solid', borderColor: 'divider',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          {/* Step dots */}
          <Stack direction="row" spacing={0.75} alignItems="center">
            {STEPS.map((_, i) => (
              <Box
                key={i}
                onClick={() => i < step && setStep(i)}
                sx={{
                  width: i === step ? 20 : 7,
                  height: 7,
                  borderRadius: 4,
                  bgcolor: i === step
                    ? theme.palette.primary.main
                    : i < step
                    ? alpha(theme.palette.primary.main, 0.35)
                    : alpha(theme.palette.action.active, 0.12),
                  transition: 'all 0.25s',
                  cursor: i < step ? 'pointer' : 'default',
                }}
              />
            ))}
          </Stack>

          <Stack direction="row" spacing={1}>
            {!isFirst && (
              <Button
                size="small"
                variant="text"
                startIcon={<ChevronLeft size={15} />}
                onClick={() => setStep(s => s - 1)}
                sx={{ fontWeight: 600, color: 'text.secondary' }}
              >
                Back
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              endIcon={!isLast && <ArrowRight size={15} />}
              onClick={handleNext}
              sx={{ fontWeight: 700, px: 2.5, borderRadius: 1.5 }}
            >
              {isLast ? 'Get Started' : 'Next'}
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
